import type {Vessel} from "#src/types/vessel.types";
import type {ButtonProps} from "antd";

import {BasicButton} from "#src/components/basic-button";
import {IconReader} from "#src/components/iconReader/IconReader";
import CustomPopover from "#src/components/Popover/CustomPopover";
import {usePopover} from "#src/components/Popover/hooks/usePopover";
import {VESSELS_CRUD_CONFIG} from "#src/config/appEntityCrud";
import {useDeviceType} from "#src/hooks/use-device-type";
import {usePreferences} from "#src/hooks/use-preferences";
import {useAppEntityCrud} from "#src/hooks/useAppEntityCrud/useAppEntityCrud";
import {useTabsStore} from "#src/store/tabs";
import { useLocalStorageState } from "ahooks";

import "./VesselPopover.scss";
import {Divider, theme, Typography} from "antd";
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router";

const {Text} = Typography;

interface SelectVesselProps extends Omit<ButtonProps, "onClick"> {
	onVesselSelect?: (vesselId: string) => void
	onCreateVessel?: () => void
}

const VesselPopover: React.FC<SelectVesselProps> = ({
																											onVesselSelect,
																											onCreateVessel,
																											style,
																											className,
																											...props
																										}) => {
	const {token} = theme.useToken();
	const navigate = useNavigate();
	const {isDark} = usePreferences();
	const {isMobile} = useDeviceType();
	const vesselsCrud = useAppEntityCrud(VESSELS_CRUD_CONFIG);
	const [vessels, setVessels] = useState<Vessel[]>([]);
	const activeKey = useTabsStore(state => state.activeKey);
	const closeOtherTabs = useTabsStore(state => state.closeOtherTabs);
	const setIsRefresh = useTabsStore(state => state.setIsRefresh);

	useEffect(() => {
		void vesselsCrud.fetchAll({page: 1, limit: 100}).then(({data}) => {
			setVessels(data);
		});
	}, [vesselsCrud.fetchAll]);

	const [selectedVessel, setSelectedVessel] = useLocalStorageState<Vessel>('selectedVessel');

	useEffect(() => {
		// Sync the local storage selected vessel with the list if it's missing
		if (!selectedVessel?.id && vessels.length > 0) {
			setSelectedVessel(vessels[0]);
		}
	}, [vessels, selectedVessel, setSelectedVessel]);

	const vesselPopover = usePopover({
		defaultOpen: false,
	});

	const handleVesselSelect = (vessel: Vessel) => {
		if (vessel.id === selectedVessel?.id) {
			vesselPopover.hide();
			return;
		}
		setSelectedVessel(vessel);
		onVesselSelect?.(vessel.id);
		vesselPopover.hide();
		// Drop other tabs' KeepAlive caches, then remount the current page with the new vessel.
		if (activeKey) {
			closeOtherTabs(activeKey);
		}
		setIsRefresh(true);
	};

	const handleCreateVessel = () => {
		onCreateVessel?.();
		navigate("/vessel");
		vesselPopover.hide();
	};

	const customButtonStyle: React.CSSProperties = {
		borderRadius: token.borderRadius,
		border: "none",
		padding: isMobile ? "6px 8px" : "8px 12px",
		height: "auto",
		width: isMobile ? "32px" : "auto",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		...style,
	};

	const popoverContent = (
		<div className="min-w-[250px]">
			<div className="">
				<div className="flex items-center gap-2">
					<IconReader
						name="vessel"
						size={28}
						className="text-primary"
					/>
					<div>
						<Text strong>
							{selectedVessel?.vessel_name || "Select Vessel"}
						</Text>
					</div>
				</div>
			</div>

			<Divider className="my-1"/>

			<div className="px-2">
				<BasicButton variant="link" type="text" size="small" className="flex items-center gap-3 px-0 justify-start" icon={<IconReader name="filesearch"/>} block>
					Ship Particular
				</BasicButton>
				<BasicButton variant="link" type="text" size="small" className="flex items-center gap-3 px-0 justify-start" icon={<IconReader name="bookmark-fill"/>} block>
					Certificates
				</BasicButton>
				<BasicButton variant="link" type="text" size="small" className="flex items-center gap-3 px-0 justify-start" icon={<IconReader name="bookmark-outline"/>} block>
					Insurance
				</BasicButton>
				<BasicButton variant="link" type="text" size="small" className="flex items-center gap-3 px-0 justify-start" icon={<IconReader name="attachment"/>} block>
					Attachment
				</BasicButton>
			</div>

			<Divider className="my-1"/>

			<div className="px-2">
				<Text type="secondary" className=" mb-1">Switch Vessel</Text>
				<div className="">
					{vessels.filter(v => v.id !== selectedVessel?.id).map(vessel => (
						<BasicButton
							type="text"
							key={vessel.id}
							className="flex items-center gap-3 justify-start px-0"
							icon={<IconReader name="vessel"/>}
							block
							onClick={() => handleVesselSelect(vessel)}
						>
							<span>
{vessel?.vessel_name}
       </span>
						</BasicButton>
					))}
				</div>
			</div>

			<Divider className="my-1"/>

			<div>
				<BasicButton
					type="default"
					onClick={handleCreateVessel}
					icon={<IconReader name="pluscircle"/>}
					block
				>
					<span>Create Vessel</span>
				</BasicButton>
			</div>
		</div>
	);

	return (
		<CustomPopover
			content={popoverContent}
			trigger="click"
			{...vesselPopover.triggerProps}
			placement="bottomLeft"
			showArrow={false}
			containerStyle={{ background: isDark ? "" : '#ffffff' }}
		>
			<BasicButton style={customButtonStyle} data-popover-open={vesselPopover.open} {...props}>
				{isMobile
? (
					<IconReader name="vessel" size={16} />
				)
: (
					<div className="flex items-center gap-2">
						<IconReader name="vessel" size={16} />
						<span>
{selectedVessel?.vessel_name || "Vessels"}
      </span>
						<IconReader name="caretdown" size={12} />
					</div>
				)}
			</BasicButton>
		</CustomPopover>
	);
};

export default VesselPopover;
